import { z } from "zod";
import {
  assetIdSchema,
  workOrderIdSchema,
  workRequestIdSchema,
} from "../lib/workIds";

export const workRequestApprovalSchema = z.object({
  id: workRequestIdSchema,
  title: z.string(),
  description: z.string(),
  severity: z.string().nullable(),
  status: z.string(),
  submittedAt: z.string(),
  submittedBy: z.string().nullable(),
  facilityId: z.number().nullable(),
  asset: z.object({
    id: z.number(),
    code: z.string().nullable(),
    name: z.string(),
    locationName: z.string().nullable(),
  }),
  convertedWorkOrderId: workOrderIdSchema.nullable(),
});

export const duplicateAnalysisSchema = z.object({
  openMatches: z.array(
    z.object({
      workOrderId: workOrderIdSchema,
      workOrderNumber: z.string(),
      status: z.string(),
      statusCode: z.string(),
      assignedTo: z.string().nullable(),
      completedBy: z.string().nullable(),
      description: z.string().nullable(),
      completedAt: z.string().nullable(),
      isOpen: z.boolean(),
      isDuplicate: z.boolean(),
      confidence: z.number(),
      rationale: z.string(),
    }),
  ),
  closedMatches: z.array(
    z.object({
      workOrderId: workOrderIdSchema,
      workOrderNumber: z.string(),
      status: z.string(),
      statusCode: z.string(),
      assignedTo: z.string().nullable(),
      completedBy: z.string().nullable(),
      description: z.string().nullable(),
      completedAt: z.string().nullable(),
      isOpen: z.boolean(),
      isDuplicate: z.boolean(),
      confidence: z.number(),
      rationale: z.string(),
    }),
  ),
  totalSameAssetWorkOrders: z.number(),
  recommendation: z.string().nullable(),
  aiAvailable: z.boolean(),
});

const workRequestDraftSchema = z
  .object({
    assetId: assetIdSchema.nullable(),
    assetName: z.string().min(1).nullable(),
    // Client-side-only field for picker prefill/retain/display — the backend
    // derives the authoritative Kind from `assetId` and never returns this, so
    // it defaults to null when parsing a server response.
    assetKind: z.enum(["ASSET", "LOCATION"]).nullable().default(null),
    workRequestDescription: z.string().min(1).nullable(),
    workRequestTitle: z.string().min(1).nullable(),
    titleIsFallback: z.boolean().optional(),
    priorityCode: z.enum(["LOW", "MEDIUM", "HIGH"]).nullable().default(null),
    categoryIds: z.array(z.number().int().positive()).optional(),
  })
  .transform((draft) => ({
    ...draft,
    // Older servers did not send the flag. Preserve edited titles when an
    // explicit false is present, and infer the old fallback title otherwise.
    titleIsFallback:
      draft.titleIsFallback ??
      draft.workRequestTitle?.trim().toLowerCase() === "new work request",
  }));

const assetCandidateSchema = z.object({
  assetId: assetIdSchema,
  assetName: z.string().min(1),
  assetCode: z.string().min(1).nullable().optional(),
  // Client-side-only: present only for candidates synthesized from a direct
  // facility asset search; the backend's Bedrock-matched candidates don't
  // carry it.
  kind: z.enum(["ASSET", "LOCATION"]).nullable().optional(),
  parentName: z.string().min(1).nullable().optional(),
  hierarchyPath: z.string().min(1).nullable().optional(),
  matchedTerms: z.array(z.string().min(1)).optional(),
  score: z.number(),
  reason: z.string().min(1),
});

export const workRequestIntakeStateSchema = z.object({
  traceId: z.string().uuid(),
  assistantMessage: z.string().min(1),
  stage: z.enum(["collect_request", "resolve_asset", "review", "created"]),
  draft: workRequestDraftSchema,
  assetCandidates: z.array(assetCandidateSchema),
  readyToCreate: z.boolean(),
  // An API that predates this field omits it; treat that as available.
  aiAssistance: z.enum(["AVAILABLE", "UNAVAILABLE"]).default("AVAILABLE"),
  status: z.enum(["READY", "NEEDS_INPUT"]).optional(),
  prompt: z
    .object({
      kind: z.literal("ASSET_PICKER"),
      candidateIds: z.array(assetIdSchema),
    })
    .nullable()
    .optional(),
});

export const createWorkRequestResponseSchema = z.object({
  workRequestId: workRequestIdSchema,
  requestNumber: z.string().min(1),
  assetId: assetIdSchema,
  workRequestTitle: z.string().min(1),
  workRequestDescription: z.string().min(1),
});

// Deliberately has no confidence/score field: this is the requester-facing,
// pre-submit duplicate check, not the admin-review-time "Reli Triage"
// duplicate analysis.
const duplicateCheckMatchBaseSchema = {
  displayId: z.string().min(1),
  title: z.string().min(1),
  asset: z
    .object({
      id: assetIdSchema,
      name: z.string().min(1),
      assetCode: z.string().nullable(),
    })
    .nullable(),
  description: z.string().nullable(),
};

const duplicateCheckMatchSchema = z.discriminatedUnion("type", [
  z.object({
    ...duplicateCheckMatchBaseSchema,
    type: z.literal("REQUEST"),
    id: workRequestIdSchema,
  }),
  z.object({
    ...duplicateCheckMatchBaseSchema,
    type: z.literal("WORK_ORDER"),
    id: workOrderIdSchema,
  }),
]);

export const assessWorkRequestResponseSchema = z.object({
  matches: z.array(duplicateCheckMatchSchema),
  // A suggestion only; null when none could be made.
  priorityCode: z.enum(["LOW", "MEDIUM", "HIGH"]).nullable(),
});

export type WorkRequestApproval = z.infer<typeof workRequestApprovalSchema>;
export type DuplicateAnalysis = z.infer<typeof duplicateAnalysisSchema>;
